import { DeleteOutlined, EditOutlined, MoreOutlined } from "@ant-design/icons";
import { Affix, Button, Card, Dropdown, Menu, Modal } from "antd";
import ListTable from "../../../common/ListTable";
import moment from "moment";
import { Fragment, useCallback, useEffect, useLayoutEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { Link, useLocation } from "react-router-dom";
import { RAW_MATERIAL_DELETE, RAW_MATERIAL_INVENTORY_LIST } from "../../../apiServices/API_ENDPOINTS";
import { deleteData, getData } from "../../../apiServices/common";
import { alertPop } from "../../../apiServices/common/helper";
import { getMovementTypeLabel } from "../../../common/helper/helper";
import {
  INVENTORY_CREATE_PERMISSION,
  INVENTORY_DELETE_PERMISSION,
  INVENTORY_EDIT_PERMISSION,
} from "../../../routes/permissions";
import { commaSeparateNumber, hasPermission } from "../../../utils/functions";
import { isMobileView } from "../../Common/AppHelper";
import BodyHeader from "../../Common/BodyHeader";
import CustomFilter from "../../Common/CustomFilter";
import Header from "../../Common/Header";
import InventoryLayOutCommon from "../LayOut/InventoryLayOutCommon";
import TrimsStoreEntryEditModal from "./TrimsStoreEntryEditModal";

const buildCleanParams = (values) => {
  if (!values || typeof values !== "object") return {};
  const out = {};
  const keys = [
    "buying_office_id",
    "sales_contract_id",
    "style_id",
    "sub_style_id",
    "movement_type",
    "reference_no",
    "challan_id",
    "supplier_id",
    "invoice_id",
    "color_id",
    "requisition_no",
    "buyer_id",
    "item_category_id",
  ];
  keys.forEach((k) => {
    const v = values[k];
    if (v !== undefined && v !== null && v !== "") out[k] = v;
  });
  if (values.entry_date) {
    const d = values.entry_date;
    out.entry_date = moment.isMoment(d) ? d.format("YYYY-MM-DD") : moment(d).format("YYYY-MM-DD");
  }
  return out;
};

const formatDateTime = (record) => {
  if (record?.unique_time) {
    const m = moment(record.unique_time, ["YYYY-MM-DD HH:mm:ss", "YYYY-MM-DD HH:mm:ss.SSS", "YYYY-MM-DD HH:mm"]);
    return m.isValid() ? m.format("YYYY-MM-DD HH:mm") : String(record.unique_time);
  }
  if (record?.entry_date) {
    const m = moment(record.entry_date, ["YYYY-MM-DD", moment.ISO_8601], true);
    return m.isValid() ? m.format("YYYY-MM-DD") : String(record.entry_date);
  }
  return "—";
};

const formatChallanInvoiceRequisition = (r) => {
  const parts = [];
  if (r.requisition_no) parts.push(`${r.requisition_no}`);
  const challanLabel = r.challan_info?.challan_no ?? r.challan_info?.name ?? r.challan_info?.reference_no;
  if (challanLabel) parts.push(`Challan: ${challanLabel}`);
  const invLabel =
    r.invoice_info?.invoice_number ?? r.invoice_info?.invoice_no ?? r.invoice_info?.number ?? (r.invoice_id ? `#${r.invoice_id}` : null);
  if (invLabel) parts.push(`Invoice: ${invLabel}`);
  if (r.reference_no && !parts.some((p) => p.includes(String(r.reference_no)))) parts.push(`Ref: ${r.reference_no}`);
  return parts.length ? parts.join(" · ") : "—";
};

const TrimsStoreEntryList = (props) => {
  const { width } = props;
  const location = useLocation();
  const routerPath = location?.pathname;
  const filterData = useSelector(({ filter }) => filter.filterData);

  const [showFilter, setShowFilter] = useState(
    localStorage.getItem("filter_status") && routerPath
      ? JSON.parse(localStorage.getItem("filter_status"))[routerPath]
      : false,
  );
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fetchError, setFetchError] = useState(null);
  const [totalData, setTotalData] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingRecordId, setEditingRecordId] = useState(null);

  const cleanFilterParams = useMemo(() => buildCleanParams(filterData), [filterData]);

  useEffect(() => {
    const filterStatus = localStorage.getItem("filter_status");
    const parsedFilterStatus = filterStatus ? JSON.parse(filterStatus) : {};
    parsedFilterStatus[routerPath] = !!showFilter;
    localStorage.setItem("filter_status", JSON.stringify(parsedFilterStatus));
  }, [showFilter, routerPath, location]);

  const fetchList = useCallback(
    async (page) => {
      setLoading(true);
      setFetchError(null);
      const params = { use_fabric_interlining_store: "No", ...cleanFilterParams };
      const response = await getData(`${RAW_MATERIAL_INVENTORY_LIST}?page=${page}`, false, params);
      if (response?.data?.code === 200) {
        const payload = response?.data?.data;
        if (Array.isArray(payload)) {
          setRows(payload);
          setTotalData(payload.length);
          setPageSize(payload.length || 10);
        } else {
          setRows(payload?.data ?? []);
          setTotalData(payload?.total ?? 0);
          setPageSize(payload?.per_page ?? 10);
        }
      } else {
        setRows([]);
        setTotalData(0);
        setFetchError("Could not load this list.");
      }
      setLoading(false);
    },
    [cleanFilterParams],
  );

  useLayoutEffect(() => {
    setCurrentPage(1);
  }, [cleanFilterParams]);

  useEffect(() => {
    fetchList(currentPage);
  }, [currentPage, fetchList]);

  const rowMenu = (record) => (
    <Menu
      onClick={({ key, domEvent }) => {
        domEvent?.stopPropagation();
        if (key === "edit" && hasPermission([INVENTORY_EDIT_PERMISSION])) {
          setEditingRecordId(record.id);
          setEditModalOpen(true);
        }
        if (key === "delete" && hasPermission([INVENTORY_DELETE_PERMISSION])) {
          Modal.confirm({
            title: "Delete this trims entry?",
            okText: "Delete",
            okType: "danger",
            onOk: async () => {
              const res = await deleteData(RAW_MATERIAL_DELETE(record.id));
              if (res && (res.code === 200 || res?.data?.code === 200)) {
                alertPop("success", "Deleted successfully");
                fetchList(currentPage);
              }
            },
          });
        }
      }}
      items={[
        { key: "edit", disabled: !hasPermission([INVENTORY_EDIT_PERMISSION]), icon: <EditOutlined />, label: "Edit" },
        {
          key: "delete",
          disabled: !hasPermission([INVENTORY_DELETE_PERMISSION]),
          icon: <DeleteOutlined />,
          label: "Delete",
          danger: true,
        },
      ]}
    />
  );

  const columns = [
    { title: "Date & Time", key: "date_time", width: 170, ellipsis: true, render: (_, r) => formatDateTime(r) },
    { title: "Buying Office", key: "buying_office", ellipsis: true, render: (_, r) => r?.buying_office_info?.name || "—" },
    { title: "Style", key: "style", width: 140, ellipsis: true, render: (_, r) => r?.style_info?.style_no || "—" },
    { title: "Color", key: "color", width: 120, ellipsis: true, render: (_, r) => r?.color_info?.name || r?.color_info?.color_name || "—" },
    {
      title: "Item Category",
      key: "item_category",
      width: 120,
      ellipsis: true,
      render: (_, r) => r?.inventory_info?.item_category_info?.name || "—",
    },
    { title: "Item", key: "item", ellipsis: true, render: (_, r) => r?.inventory_info?.item_info?.name || "—" },
    { title: "Unit", key: "unit", width: 72, ellipsis: true, render: (_, r) => r?.inventory_info?.unit_info?.name || "—" },
    {
      title: "Movement Type",
      dataIndex: "movement_type",
      key: "movement_type",
      width: 160,
      ellipsis: true,
      render: (v) => getMovementTypeLabel(v) || v || "—",
    },
    { title: "Challan/Invoice/Requisition", key: "cir", width: 240, ellipsis: true, render: (_, r) => formatChallanInvoiceRequisition(r) },
    {
      title: "Quantity",
      dataIndex: "received_total_quantity",
      key: "received_total_quantity",
      width: 100,
      align: "right",
      render: (v) => (v !== undefined && v !== null ? commaSeparateNumber(v) : "—"),
    },
    {
      title: "Action",
      key: "action",
      width: 72,
      align: "center",
      fixed: "right",
      render: (_, record) => (
        <Dropdown overlay={rowMenu(record)} trigger={["click"]}>
          <MoreOutlined style={{ fontSize: 18, cursor: "pointer" }} />
        </Dropdown>
      ),
    },
  ];

  const cardWidth = isMobileView() ? "100%" : width ? `${width}px` : "100%";
  const memoizedCustomFilter = useMemo(
    () => (
      <CustomFilter
        showFilter={showFilter}
        tableData={rows}
        fetchTableData={() => setCurrentPage(1)}
        filterName="trims_store_entry"
      />
    ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [showFilter],
  );

  return (
    <Fragment>
      <InventoryLayOutCommon>
        <Affix offsetTop={0}>
          <Card className="p-fixed" style={{ width: cardWidth }}>
            <Header
              title="Trims and accessories store entry"
              subTitle=""
              actions={[
                <Button
                  type={showFilter ? "danger" : "primary"}
                  size="small"
                  onClick={() => setShowFilter((prev) => !prev)}
                  key="filter"
                  style={{ float: "right" }}
                >
                  {`${showFilter ? "Close" : "Open"} filter`}
                </Button>,
                <Button key="add" size="small" type="primary" disabled={!hasPermission([INVENTORY_CREATE_PERMISSION])}>
                  <Link to="/inventory/store/trims-store-entry/add">
                    {hasPermission([INVENTORY_CREATE_PERMISSION]) ? "Add new" : "No permission"}
                  </Link>
                </Button>,
              ]}
            />
          </Card>
        </Affix>

        <div className="containt-body">
          <Card>
            <BodyHeader searchFilter={() => {}} />
            {memoizedCustomFilter}
            <ListTable
              bordered
              size="small"
              loading={loading}
              error={fetchError}
              onRetry={() => fetchList(currentPage)}
              emptyText="No records to show yet."
              dataSource={rows}
              rowKey="id"
              columns={columns}
              scroll={{ x: 1650 }}
              pagination={{
                showSizeChanger: false,
                current: currentPage,
                total: totalData,
                pageSize,
                onChange: (page) => setCurrentPage(page),
                showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
              }}
            />
          </Card>
        </div>

        <TrimsStoreEntryEditModal
          open={editModalOpen}
          recordId={editingRecordId}
          onClose={() => {
            setEditModalOpen(false);
            setEditingRecordId(null);
          }}
          onSaved={() => fetchList(currentPage)}
        />
      </InventoryLayOutCommon>
    </Fragment>
  );
};

export default TrimsStoreEntryList;
